import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import PlayIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heading } from '../../components/Heading';
import { Spinner } from '../../components/Spinner';
import { useAuth } from '../../contexts/authContext';
import GameCustomizeDrawer from './GameCustomizeDrawer';
import GameLeaderboardDrawer from './GameLeaderboardDrawer';

const GamePlayDrawer = lazy(() => import('./GamePlayDrawer'));

const menuRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 24,
};

// Shown in place of the menu (and all sub-drawers) when an unauthenticated
// visitor lands anywhere under /game. Highscores, achievements and the
// per-throw counters all key off the user's Firestore document, so the
// whole game section is gated behind a signed-in user — without one, a
// played run would just evaporate the moment they navigate away. The
// "Logga in" CTA records the current path as `prevPathname` so closing
// the login drawer drops the player back here (a successful sign-in
// sends them home — that's the existing app-wide behaviour of
// <SignInForm /> and out of scope for this gate).
function LoginRequired() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLoginClick = () => {
    navigate('/login', { state: { prevPathname: location.pathname } });
  };

  return (
    <Stack gap={3} alignItems="center" textAlign="center" mt={2} px={2}>
      <Box
        sx={{
          width: 72,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: 'action.hover',
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
      </Box>
      <Stack gap={1}>
        <Typography variant="h6" fontWeight={700}>
          Logga in för att spela
        </Typography>
        <Typography color="text.secondary">
          Du behöver vara inloggad för att dina resultat ska sparas.
        </Typography>
      </Stack>
      <Button variant="contained" onClick={handleLoginClick}>
        Logga in
      </Button>
    </Stack>
  );
}

export default function GamePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname: string = location.pathname;
  const prevPathname: string | undefined = location.state?.prevPathname;
  const isBehindUserDrawerFromLeaderboard =
    (pathname.startsWith('/user') || pathname.startsWith('/profile')) &&
    prevPathname === '/game/leaderboard';

  // Gate the whole /game/* section on auth. While Firebase is still
  // resolving (e.g. on a hard refresh) we show a spinner instead of
  // flashing the login prompt to a player who's actually signed in.
  // When unauthenticated, the menu cards AND all sub-drawers are skipped
  // — direct URL navigation to /game/play, /game/leaderboard, etc. lands
  // on the same prompt, so there's no way to slip past the gate.
  const { isUserLoggedIn, loading } = useAuth();

  return (
    <Container component="main" maxWidth="sm">
      <Stack gap={4} p={2}>
        <Heading>Spel</Heading>

        {isUserLoggedIn ? (
          <>
            <Card>
              <CardActionArea
                onClick={() => navigate('play')}
                sx={{ p: 2, minHeight: 64 }}
              >
                <div style={menuRowStyle}>
                  <PlayIcon />
                  <Typography>Spela</Typography>
                </div>
              </CardActionArea>
            </Card>

            <Card>
              <CardActionArea
                onClick={() => navigate('leaderboard')}
                sx={{ p: 2, minHeight: 64 }}
              >
                <div style={menuRowStyle}>
                  <LeaderboardIcon />
                  <Typography>Topplista</Typography>
                </div>
              </CardActionArea>
            </Card>

            <Card>
              <CardActionArea
                onClick={() => navigate('customize')}
                sx={{ p: 2, minHeight: 64 }}
              >
                <div style={menuRowStyle}>
                  <PaletteIcon />
                  <Typography>Anpassa</Typography>
                </div>
              </CardActionArea>
            </Card>

            <Suspense fallback={null}>
              <GamePlayDrawer
                open={pathname === '/game/play'}
                onClose={() => navigate('/game')}
              />
            </Suspense>

            <GameLeaderboardDrawer
              open={
                pathname === '/game/leaderboard' ||
                isBehindUserDrawerFromLeaderboard
              }
              onClose={() => navigate('/game')}
            />

            <GameCustomizeDrawer
              open={pathname === '/game/customize'}
              onClose={() => navigate('/game')}
            />
          </>
        ) : loading ? (
          <Spinner />
        ) : (
          <LoginRequired />
        )}
      </Stack>
    </Container>
  );
}
